"use client"

import { useState, type ReactNode } from "react"
import { getGooglePreferredSource, warmGooglePreferredSource } from "@/lib/google-preferred-source"
import { openPreferredSourcePopup } from "@/lib/preferred-source"
import posthog from "posthog-js"

/**
 * FRIDGE-611: every press of the preferred-source button is measured with the
 * reader's experiment bucket (prompt / control / none), so the slide-in
 * prompt's lift is read against the control group's clicks on the in-article
 * button. A press from the prompt itself also retires the prompt for good.
 *
 * The experiment's state helpers load on the press, never with the page: this
 * button sits in the footer of every page, and a static import put the whole
 * prompt library (~1.2 kB) into every page's First Load JS, which tipped
 * /compare over its bundle budget (09/27/2026). Google's popup still opens
 * synchronously in the same click, so popup blocking is unaffected; only the
 * analytics event waits for the helpers.
 */
function recordPreferredSourceClick(surface: string) {
  const path = window.location.pathname
  void import("@/lib/preferred-source-prompt")
    .then(({ PROMPT_STORAGE_KEY, parsePromptState, serializePromptState }) => {
      let bucket: string = "none"
      try {
        const state = parsePromptState(window.localStorage.getItem(PROMPT_STORAGE_KEY))
        if (state) {
          bucket = state.bucket
          if (surface === "prompt") window.localStorage.setItem(PROMPT_STORAGE_KEY, serializePromptState({ ...state, clickedAt: Date.now() }))
        }
      } catch {
        /* storage unavailable: still measured, bucket "none" */
      }
      posthog?.capture?.('preferred_source_clicked', { surface, bucket, path })
    })
    .catch(() => {
      /* helpers failed to load: still measured, bucket "none" */
      posthog?.capture?.('preferred_source_clicked', { surface, bucket: "none", path })
    })
}

/** Shared button; Google owns the preference UI. The legacy export name keeps existing mounts compatible. */
export function PreferredSourceLink({ className = "", children, title, ariaLabel, surface = "inline" }: {
  className?: string; children: ReactNode; title?: string; ariaLabel?: string; rel?: string
  /** Where the button sits, for measurement (FRIDGE-611). */
  surface?: string
}) {
  const [blocked, setBlocked] = useState(false)
  // Google's SDK is fetched on reader intent, not on mount — see
  // warmGooglePreferredSource() for why (its frame logs a CSP message on every
  // page load, which the axe gate counts). Pointer, keyboard and touch each
  // warm it; a click that arrives before the SDK takes the synchronous popup
  // fallback below, exactly as it always did on a slow network.
  return (
    <>
      <button type="button" className={`cursor-pointer text-left ${className}`} title={title} aria-label={ariaLabel}
        aria-haspopup="dialog"
        onPointerEnter={warmGooglePreferredSource}
        onFocus={warmGooglePreferredSource}
        onTouchStart={warmGooglePreferredSource}
        onClick={() => {
          recordPreferredSourceClick(surface)
          const sdk = getGooglePreferredSource()
          // Google derives the source from the current page URL. Local previews
          // use its documented Fridge.com deeplink instead of submitting localhost.
          if (sdk && /(^|\.)fridge\.com$/i.test(window.location.hostname)) {
            setBlocked(false)
            sdk.addPreferredSource()
          } else {
            setBlocked(!openPreferredSourcePopup())
          }
        }}>
        {children}
      </button>
      {blocked && <span role="status" className="block text-xs text-gray-600">Allow Google popups, then try again.</span>}
    </>
  )
}
